import type { Recolor } from '../body';
import type { Headwear } from '../headwear';
import type { Outfit } from '../outfits';
import type { SetFlair } from './mythic';

/**
 * A set's art (ADR-0112): a costume in three pieces, keyed like its set (content `SETS`, or a season's, ADR-0123) and its
 * hat, outfit and gun skin.
 * `head`: a hat that flies off or headwear tied on (headwear.ts); `outfit`: the suit, its top and, through `legs`, the
 * trousers and boots (outfits.ts); `gun`: the finish its guns are painted in (sprites/weapons/skins.ts).
 * Each set is a costume, so it changes the gardener's shape at game zoom, and the team still reads on it: every piece
 * keeps parts in the team's keys (`b B c`), recoloured per team.
 */
export interface SetArt {
  head: Headwear;
  outfit: Outfit;
  gun: Recolor;
  /** A mythic set's (ADR-0160): what each piece does on its own, drawn at run time round the gardener (`flair`). */
  flair?: SetFlair;
}

/**
 * A set's art, its head and suit drawn the first time each is asked for (ADR-0217): every set's pictures drawn as the
 * page loaded cost a phone half a second, and a gardener wears one set at most.
 */
export function drawnLater({
  head,
  outfit,
  ...rest
}: Omit<SetArt, 'head' | 'outfit'> & { head: () => Headwear; outfit: () => Outfit }): SetArt {
  let drawnHead: Headwear | undefined;
  let drawnOutfit: Outfit | undefined;
  return {
    ...rest,
    get head() {
      drawnHead ??= head();
      return drawnHead;
    },
    get outfit() {
      drawnOutfit ??= outfit();
      return drawnOutfit;
    },
  };
}
